import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

const mapCompTypeToEnum = (compTypeString) => {
  switch (compTypeString) {
    case 'rent':
      return 0;
    case 'sale':
      return 1;
    case 'sold':
      return 2;
  }
}

export const initialState = {
  //compsCache: {}, // caching the comps fetched from the service, structure: { task.id: { selectedCompDataType (rent, sale, sold): } }

  // SHARED WITH AUCTION UW?
  compsImageCache: {}, // dictionary of all fetched images from all selected markers
  compsImageRequests: [],

  postAcqUWClientTask: null,
  marketData: null,
  selectedOpportunityCensusTracts: [],
  selectedOpportunityMsrHomes: [],
  selectedOpportunityImportProperty: null,
  postAcqProformaIsOpen: true,
  selectedCT: 'amherstEstimatedAMI',
  ctMouseoverData: null,
  showCTMouseoverData: true,
  ctLegendData: null,
  selectedCompDataType: 'rent',
  selectedProformaInputsContainer: null,
  selectedValuation: null,
  transientValuation: null,
  lastCompletedValuation: null,
  valuationIsDirty: false,
  hasInputLimitError: { rent: false, afterRepairValue: false },
  saveUWDialogParameters: { dialogIsOpen: false, route: null },
  selectedOpportunityComps: [],
  selectedOpportunityImages: [],
  proformaWidth: 325,
  isSavingUW: false,
  postAcqUWMapState: {
    zoom: 4,
    center: { lat: 30.2672, lng: -97.7431 },
    gestureHandling: 'auto', // Gesture Handling instead of scrollwheel for CTRL + Scroll [auto/cooperative]
    mapViewType: 'hybrid', // [hybrid/roadmap]
    autoNav: false,
    selectedMarkerLabel: 'rentprice',
    isDrawing: false,
    polygon: null,
    initialized: false
  },
  postAcqUWMarkerState: {
    selectedMarker: null,
    isInfoBoxOpen: false,
    isFetchingImages: false,
    selectedMarkerImages: [],
    uncapturedMarkers: []
  },
  propertyUWMsrHomesMapState: {
    zoom: 4,
    center: { lat: 30.2672, lng: -97.7431 },
    gestureHandling: 'auto',
    mapViewType: 'hybrid',
    autoNav: false,
    initialized: false
  },
  propertyUWMsrHomesMarkerState: {
    selectedMarker: null
  },
  propertyGridCurrentViewData: [],   // Used to filter the map based on grid filters
  propertyGridRowIdToFocus: null,
  propertyUWMsrHomesGridCurrentViewData: [],
  cmaData: null,
  cmaStatus: null,      // 'Found' | 'NotFound' | 'Failed' — distinguishes a missing CMA from a failed API call
  cmaMessage: null,     // troubleshooting detail, surfaced for every status
  isFetchingCma: false,
  cmaDialogIsOpen: false,
  isFetchingContainers: false,
  isFetchingMsrHomes: false,
  isFetchingPostAcqUWData: false,
  isFetchingPostAcqUWImages: false,
  isFetchingTransactionEvents: false,
  isFetchingTaskHistory: false,
  selectedContainerId: null,
  selectedContainer: null,
  viewContainersDialogIsOpen: false,
  containers: null,
  selectedTaskHistory: null,
  selectedTaskEventHistory: null,
  selectedTaskHistoryTab: 'tasks',
  viewUnderwriteModalIsOpen: false,
  underwriteModalState: {
    postAcqUWClientTask: null,
    selectedValuation: null,
    transientValuation: null,
    selectedCT: 'amherstEstimatedAMI',
    ctMouseoverData: null,
    showCTMouseoverData: true,
    ctLegendData: null,
    selectedCompDataType: 'rent',
    selectedProformaInputsContainer: null,
    selectedOpportunityComps: [],
    selectedOpportunityAmherstIndicative: null,
    marketData: null,
    selectedContainer: null,
    postAcqUWMapState: {
      zoom: 4,
      center: { lat: 30.2672, lng: -97.7431 },
      gestureHandling: 'auto', // Gesture Handling instead of scrollwheel for CTRL + Scroll [auto/cooperative]
      mapViewType: 'hybrid', // [hybrid/roadmap]
      autoNav: false,
      selectedMarkerLabel: 'rentprice',
      isDrawing: false,
      polygon: null,
      initialized: false
    },
    postAcqUWMarkerState: {
      selectedMarker: null,
      isInfoBoxOpen: false,
      isFetchingImages: false,
      selectedMarkerImages: [],
      uncapturedMarkers: []
    },
    propertyGridCurrentViewData: [],   // Used to filter the map based on grid filters
    propertyGridRowIdToFocus: null,
    isFetchingPostAcqUWData: false
  }
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'postAcqUWSlice',
  initialState,
  reducers: {
    setPostAcqUWClientTask: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.postAcqUWClientTask = action.payload.postAcqUWClientTask;
        } else {
          state.postAcqUWClientTask = action.payload.postAcqUWClientTask;
        }
      },
      prepare: (postAcqUWClientTask, isUWModal) => ({ payload: { postAcqUWClientTask, isUWModal }})
    },
    setPostAcqUWMarketData: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.marketData = action.payload.marketData;
        } else {
          state.marketData = action.payload.marketData;
        }
      },
      prepare: (marketData, isUWModal) => ({ payload: { marketData, isUWModal }})
    },
    setPostAcqUWCompsImageCache: (state, action) => {
      state.compsImageCache = action.payload;
    },
    setPostAcqUWCompsImageRequests: (state, action) => {
      state.compsImageRequests = action.payload;
    },
    setPostAcqSelectedOpportunityCensusTracts: (state, action) => {
      state.selectedOpportunityCensusTracts = action.payload;
    },
    setPostAcqUWMsrHomes: (state, action) => {
      state.selectedOpportunityMsrHomes = action.payload;
    },
    setPostAcqSelectedOpportunityAmherstIndicative: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.selectedOpportunityAmherstIndicative = action.payload.selectedOpportunityAmherstIndicative;
        } else {
          state.selectedOpportunityAmherstIndicative = action.payload.selectedOpportunityAmherstIndicative;
        }
      },
      prepare: (selectedOpportunityAmherstIndicative, isUWModal) => ({ payload: { selectedOpportunityAmherstIndicative, isUWModal } })
    },
    togglePostAcqProformaIsOpen: (state, action) => {
      state.postAcqProformaIsOpen = !state.postAcqProformaIsOpen;
    },
    setPostAcqSelectedCT: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.selectedCT = action.payload.selectedCT;
        } else {
          state.selectedCT = action.payload.selectedCT;
        }
      },
      prepare: (selectedCT, isUWModal) => ({ payload: { selectedCT, isUWModal } })
    },
    setPostAcqCTMouseoverData: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.ctMouseoverData = action.payload.ctMouseoverData;
        } else {
          state.ctMouseoverData = action.payload.ctMouseoverData;
        }
      },
      prepare: (ctMouseoverData, isUWModal) => ({ payload: { ctMouseoverData, isUWModal } })
    },
    setPostAcqShowCTMouseoverData: {
      reducer: (state, action) => { // State whether or not the show census tract summary information on hover
        if (action.payload.isUWModal) {
          state.underwriteModalState.showCTMouseoverData = action.payload.showCTMouseoverData;
        } else {
          state.showCTMouseoverData = action.payload.showCTMouseoverData;
        }
      },
      prepare: (showCTMouseoverData, isUWModal) => ({ payload: { showCTMouseoverData, isUWModal } })
    },
    setPostAcqCTLegendData: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.ctLegendData = action.payload.ctLegendData;
        } else {
          state.ctLegendData = action.payload.ctLegendData;
        }
      },
      prepare: (ctLegendData, isUWModal) => ({ payload: { ctLegendData, isUWModal } })
    },
    setPostAcqSelectedCompDataType: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.selectedCompDataType = action.payload.selectedCompDataType;
        } else {
          state.selectedCompDataType = action.payload.selectedCompDataType;
        }
      },
      prepare: (selectedCompDataType, isUWModal) => ({ payload: { selectedCompDataType, isUWModal } })
    },
    setPostAcqSelectedProformaInputsContainer: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.selectedProformaInputsContainer = action.payload.selectedProformaInputsContainer;
        } else {
          state.selectedProformaInputsContainer = action.payload.selectedProformaInputsContainer;
        }
      },
      prepare: (selectedProformaInputsContainer, isUWModal) => ({ payload: { selectedProformaInputsContainer, isUWModal } })
    },
    setPostAcqSelectedValuation: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.selectedValuation = action.payload.selectedValuation;
        } else {
          state.selectedValuation = action.payload.selectedValuation;
        }
      },
      prepare: (selectedValuation, isUWModal) => ({ payload: { selectedValuation, isUWModal } })
    },
    setPostAcqTransientValuation: { // Triggers setPostAcqTransientValuationSaga
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.transientValuation = action.payload.transientValuation;
        } else {
          state.transientValuation = action.payload.transientValuation;
        }
      },
      prepare: (transientValuation, resetInputDirtyFlags, isUWModal) => ({ payload: { transientValuation, resetInputDirtyFlags, isUWModal } })
    },
    setPostAcqValuationIsDirty: (state, action) => { // NOTE: valuationIsDirty and shouldCalculateProforma flags work together to enbale/disable 'Save' and  'Calculate' buttons in PropertyUWProforma
      state.valuationIsDirty = action.payload;
    },
    setPostAcqHasInputLimitError: {
      reducer: (state, action) => {
        state.hasInputLimitError[action.payload.inputGroup] = action.payload.hasError;
      },
      prepare: (inputGroup, hasError) => ({ payload: { inputGroup, hasError }})
    },
    setPostAcqSaveUWDialogParameters: (state, action) => {
      state.saveUWDialogParameters = action.payload;
    },
    setPostAcqSelectedOpportunityComps: {
      reducer: (state, action) => {
        const { selectedOpportunityComps, isUWModal } = action.payload;
        const toUpdate = isUWModal ? state.underwriteModalState.selectedOpportunityComps : state.selectedOpportunityComps;
        if (toUpdate.length === 0 && selectedOpportunityComps.length === 0) return;

        if (isUWModal) {
          state.underwriteModalState.selectedOpportunityComps = [...selectedOpportunityComps];
        } else {
          state.selectedOpportunityComps = [...selectedOpportunityComps];
        }
      },
      prepare: (selectedOpportunityComps, isUWModal) => ({ payload: { selectedOpportunityComps, isUWModal } })
    },
    setPostAcqSelectedOpportunityImages: (state, action) => {
      if (state.selectedOpportunityImages.length !== 0 || action.payload.length !== 0) {
        state.selectedOpportunityImages = [...action.payload];
      }
    },
    setPostAcqProformaWidth: (state, action) => {
      state.proformaWidth = action.payload;
    },
    setIsSavingPostAcqUW: (state, action) => {
      state.isSavingUW = action.payload;
    },
    setPostAcqUWMapState: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.postAcqUWMapState = { ...state.underwriteModalState.postAcqUWMapState, ...action.payload.postAcqUWMapState };
        } else {
          state.postAcqUWMapState = { ...state.postAcqUWMapState, ...action.payload.postAcqUWMapState };
        }
      },
      prepare: (postAcqUWMapState, isUWModal) => ({ payload: { postAcqUWMapState, isUWModal } })
    },
    setPostAcqUWMarkerState: {
      reducer: (state, action) => { // Triggers getImagesSaga
        if (action.payload.isUWModal) {
          state.underwriteModalState.postAcqUWMarkerState = { ...state.underwriteModalState.postAcqUWMarkerState, ...action.payload.postAcqUWMarkerState };
        } else {
          state.postAcqUWMarkerState = { ...state.postAcqUWMarkerState, ...action.payload.postAcqUWMarkerState };
        }
      },
      prepare: (postAcqUWMarkerState, isUWModal) => ({ payload: { postAcqUWMarkerState, isUWModal } })
    },
    setPostAcqUWMsrHomesMapState: (state, action) => {
      state.propertyUWMsrHomesMapState = { ...state.propertyUWMsrHomesMapState, ...action.payload };
    },
    setPostAcqUWMsrHomesMarkerState: (state, action) => { // Triggers getImagesSaga
      state.propertyUWMsrHomesMarkerState = { ...state.propertyUWMsrHomesMarkerState, ...action.payload };
    },
    setPostAcqPropertyGridCurrentViewData: {
      reducer: (state, action) => { // Used to filter the map when grid filter is applied
        if (action.payload.isUWModal) {
          state.underwriteModalState.propertyGridCurrentViewData = action.payload.propertyGridCurrentViewData;
        } else {
          state.propertyGridCurrentViewData = action.payload.propertyGridCurrentViewData;
        }
      },
      prepare: (propertyGridCurrentViewData, isUWModal) => ({ payload: { propertyGridCurrentViewData, isUWModal } })
    },
    setPostAcqPropertyGridRowIdToFocus: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.propertyGridRowIdToFocus = action.payload.propertyGridRowIdToFocus;
        } else {
          state.propertyGridRowIdToFocus = action.payload.propertyGridRowIdToFocus;
        }
      },
      prepare: (propertyGridRowIdToFocus, isUWModal) => ({ payload: { propertyGridRowIdToFocus, isUWModal } })
    },
    setPostAcqUWMsrHomesGridCurrentViewData: (state, action) => {
      state.propertyUWMsrHomesGridCurrentViewData = action.payload;
    },
    setIsFetchingPostAcqContainers: (state, action) => {
      state.isFetchingContainers = action.payload;
    },
    setIsFetchingPostAcqUWMsrHomes: (state, action) => {
      state.isFetchingMsrHomes = action.payload;
    },
    setIsFetchingPostAcqUWData: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.isFetchingPostAcqUWData = action.payload.isFetchingPostAcqUWData;
        } else {
          state.isFetchingPostAcqUWData = action.payload.isFetchingPostAcqUWData;
        }
      },
      prepare: (isFetchingPostAcqUWData, isUWModal) => ({ payload: { isFetchingPostAcqUWData, isUWModal } })
    },
    setIsFetchingPostAcqUWImages: (state, action) => {
      state.isFetchingPostAcqUWImages = action.payload;
    },
    setPostAcqSelectedContainerId: {
      reducer: (state, action) => {
        state.selectedContainerId = action.payload.selectedContainerId;
      },
      prepare: (selectedContainerId, transactionOpportunityId, shouldEndLoading) => ({ payload: { selectedContainerId, transactionOpportunityId, shouldEndLoading } })
    },
    setPostAcqSelectedContainer: {
      reducer: (state, action) => {
        if (action.payload.isUWModal) {
          state.underwriteModalState.selectedContainer = action.payload.selectedContainer;
        } else {
          state.selectedContainer = action.payload.selectedContainer;
        }
      },
      prepare: (selectedContainer, isUWModal) => ({ payload: { selectedContainer, isUWModal } })
    },
    setPostAcqViewContainersDialogIsOpen: (state, action) => {
      state.viewContainersDialogIsOpen = action.payload;
    },
    setPostAcqContainers: (state, action) => {
      state.containers = action.payload;
    },
    setIsFetchingPostAcqTransactionEvents: (state, action) => {
      state.isFetchingTransactionEvents = action.payload;
    },
    setIsFetchingPostAcqTaskHistory: (state, action) => {
      state.isFetchingTaskHistory = action.payload;
    },
    setPostAcqSelectedTaskHistory: (state, action) => {
      state.selectedTaskHistory = action.payload;
    },
    setPostAcqSelectedTaskEventHistory: (state, action) => {
      state.selectedTaskEventHistory = action.payload;
    },  
    setPostAcqSelectedTaskHistoryTab: (state, action) => {
      state.selectedTaskHistoryTab = action.payload
    },
    setPostAcqViewUnderwriteModalIsOpen: (state, action) => {
      state.viewUnderwriteModalIsOpen = action.payload
    },
    setPostAcqCmaResult: (state, action) => {
      const { status, message, data } = action.payload || {};
      state.cmaStatus = status ?? 'Failed';
      state.cmaMessage = message ?? null;
      state.cmaData = data ?? null;
    },
    setIsFetchingPostAcqCma: (state, action) => {
      state.isFetchingCma = action.payload;
    },
    setPostAcqCmaDialogIsOpen: (state, action) => {
      state.cmaDialogIsOpen = action.payload;
    },

    // Redux saga actions, no reducers
    fetchPostAcqTaskData: { // Triggers postAcqTaskSaga
      reducer: () => { },
      prepare: (taskId, selectedContainerId, history) => ({ payload: { taskId, selectedContainerId, history } })
    },
    fetchPostAcqUnderwriteModalData: { // Triggers postAcqTaskSaga
      reducer: () => { },
      prepare: (taskId, loadInstanceId, valuationId) => ({ payload: { taskId, loadInstanceId, valuationId } })
    },
    fetchPostAcqTaskHistory: { // Triggers getTasksForTaskHistorySaga
      reducer: () => { },
      prepare: (taskId, callback) => ({ payload: { taskId, callback } })
    },
    fetchPostAcqUWCensusTract: () => { }, // Triggers postAcqTaskSaga
    fetchPostAcqUWContainers: () => { }, // Triggers postAcqTaskSaga
    fetchPostAcqUWImages: () => { }, // Triggers postAcqTaskSaga
    fetchPostAcqUWMsrHomes: () => { }, // Triggers getMsrHomesSaga
    fetchPostAcqCma: () => { }, // Triggers fetchPostAcqCma saga in postAcqTaskSaga
    savePostAcqModuleUW: () => { }, // Triggers savePostAcqUWSaga
    afterSavePostAcqUWNavigate: () => { }, // Triggers afterSavePostAcqUWNavigateSaga
    setPostAcqTransientValuationInputs: { // Triggers setPostAcqTransientValuationSaga
      reducer: () => { },
      prepare: (valuationInput, skipCalculate) => ({ payload: { valuationInput, skipCalculate } })
    },
    setPostAcqTransientValuationNotes: () => { }, // Triggers setPostAcqTransientValuationSaga
    calcPropertyUWProforma: () => { }, // Triggers calcProformaSaga
    toggleIsSelectedAsPostAcqComp: { // Triggers toggleIsSelectedAsPostAcqCompSaga
      reducer: () => { },
      prepare:  (compId, viewingReadonlyMode) => ({ payload: { compId, viewingReadonlyMode }})
    },
    takePostAcqValuation: () => { } // Triggers postAcqTakeValuationSaga
  }
});

export default slice.reducer;

export const postAcqUWActions = slice.actions;

// Memoized selectors
export const postAcqUWSelectors = {
  postAcqUWStateSelector: createSelector(
    state => state.postAcqUWState,
    postAcqUWState => postAcqUWState
  ),
  postAcqUWCompsImageCacheSelector: createSelector(
    state => state.postAcqUWState.compsImageCache,
    compsImageCache => compsImageCache
  ),
  postAcqUWCompsImageRequestsSelector: createSelector(
    state => state.postAcqUWState.compsImageRequests,
    compsImageRequests => compsImageRequests
  ),
  postAcqTransientValuationSelector: createSelector(
    state => state.postAcqUWState.transientValuation,
    transientValuation => transientValuation
  ),
  selectedPostAcqValuationSelector: createSelector(
    state => state.postAcqUWState.selectedValuation,
    selectedValuation => selectedValuation
  ),
  shouldCalculateProformaSelector: createSelector(
    state => state.propertyUWState.shouldCalculateProforma,
    shouldCalculateProforma => shouldCalculateProforma
  ),
  selectedPostAcqOpportunityCompsSelector: createSelector( // Full collection of comps for the selectedOpportunity
    (state, viewingReadonlyMode) => viewingReadonlyMode ? state.postAcqUWState.underwriteModalState.selectedOpportunityComps : state.postAcqUWState.selectedOpportunityComps,
    selectedOpportunityComps => selectedOpportunityComps
  ),
  filteredSelectedPostAcqOpportunityCompsSelector: createSelector( // Collection of comps for the selectedOpportunity filtered by type (Rent/For Sale/Sold)
    [state => state.postAcqUWState.selectedOpportunityComps, state => state.postAcqUWState.selectedCompDataType],
    (selectedOpportunityComps, selectedCompDataType) => {
      return selectedOpportunityComps.filter(comp => comp.compType === mapCompTypeToEnum(selectedCompDataType))
    }
  ),
  modalFilteredSelectedPostAcqOpportunityCompsSelector: createSelector( // Collection of comps for the selectedOpportunity filtered by type (Rent/For Sale/Sold)
    [state => state.postAcqUWState.underwriteModalState.selectedOpportunityComps, state => state.postAcqUWState.underwriteModalState.selectedCompDataType],
    (selectedOpportunityComps, selectedCompDataType) => {
      return selectedOpportunityComps.filter(comp => comp.compType === mapCompTypeToEnum(selectedCompDataType))
    }
  ),
  isFetchingPostAcqUWDataSelector: createSelector(
    state => state.postAcqUWState.isFetchingPostAcqUWData,
    isFetchingPostAcqUWData => isFetchingPostAcqUWData
  ),
  isSyncingPurchaseStateSelector: createSelector(
    state => state.propertyUWState.isSyncingPurchaseState,
    isSyncingPurchaseState => isSyncingPurchaseState
  ),
  selectedContainerIdSelector: createSelector(
    state => state.propertyUWState.selectedContainerId,
    selectedContainerId => selectedContainerId
  )
}